import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError, fileUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CheckCircle2, XCircle, KeyRound, Clock, ArrowRightLeft } from "lucide-react";

const STATUS_BADGE = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-library/10 text-library",
    borrowed: "bg-blue-100 text-blue-800",
    returned: "bg-ink/10 text-ink/70",
    declined: "bg-red-100 text-red-800",
};

export default function Reservations() {
    const { refreshUser } = useAuth();
    const [tab, setTab] = useState("borrowing"); // borrowing | lending
    const [borrowing, setBorrowing] = useState([]);
    const [lending, setLending] = useState([]);

    const load = async () => {
        try {
            const [b, l] = await Promise.all([
                api.get("/reservations/borrowing"),
                api.get("/reservations/lending"),
            ]);
            setBorrowing(b.data); setLending(l.data);
        } catch (e) { toast.error(formatError(e)); }
    };
    useEffect(() => { load(); }, []);

    const list = tab === "borrowing" ? borrowing : lending;

    const doAction = async (fn, successMsg) => {
        try {
            await fn();
            if (successMsg) toast.success(successMsg);
            await Promise.all([load(), refreshUser()]);
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-10">
            <div className="mb-8">
                <div className="label-mini">Reservations</div>
                <h1 className="font-serif text-4xl font-semibold">Requests & OTP handovers</h1>
                <p className="text-ink/60 mt-2">Track what you&apos;re borrowing and what you&apos;re lending out.</p>
            </div>

            <div className="inline-flex rounded-full bg-paper border border-parchment p-1 mb-6">
                <TabBtn active={tab === "borrowing"} onClick={() => setTab("borrowing")} testid="tab-borrowing" label={`Borrowing (${borrowing.length})`} />
                <TabBtn active={tab === "lending"} onClick={() => setTab("lending")} testid="tab-lending" label={`Lending (${lending.length})`} />
            </div>

            {list.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-parchment rounded-2xl p-12 text-center" data-testid="reservations-empty">
                    <div className="h-14 w-14 mx-auto rounded-full bg-paper flex items-center justify-center mb-4">
                        <ArrowRightLeft className="h-6 w-6 text-ink/40" />
                    </div>
                    <div className="font-serif text-xl mb-1">Nothing here yet</div>
                    <p className="text-sm text-ink/60">
                        {tab === "borrowing" ? "Browse the storefront and reserve a book to see it here." : "When classmates request your books, they will show up here for approval."}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {list.map((r) => (
                        <ReservationCard
                            key={r.id}
                            r={r}
                            role={tab === "borrowing" ? "borrower" : "owner"}
                            onAction={doAction}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, label, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}
        >
            {label}
        </button>
    );
}

function ReservationCard({ r, role, onAction }) {
    const [otp, setOtp] = useState("");
    const cover = r.book?.cover_file_id;

    return (
        <div data-testid={`res-card-${r.id}`} className="bg-white border border-parchment rounded-2xl p-5 shadow-sm">
            <div className="flex flex-wrap gap-5 items-start">
                <div className="w-20 h-28 bg-paper rounded-md border border-parchment overflow-hidden flex-shrink-0">
                    {cover ? (
                        <img src={fileUrl(cover)} alt={r.book.title} className="w-full h-full object-cover" />
                    ) : null}
                </div>
                <div className="flex-1 min-w-[220px]">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-serif text-xl font-semibold">{r.book?.title}</div>
                            <div className="text-sm text-ink/60">by {r.book?.author} · {r.book?.price_per_day} Zapp/day</div>
                        </div>
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${STATUS_BADGE[r.status]}`} data-testid={`res-status-${r.id}`}>
                            {r.status}
                        </span>
                    </div>
                    <div className="text-xs text-ink/60 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                        {role === "borrower" ? (
                            <span><span className="uppercase tracking-widest font-bold">Owner · </span>{r.owner?.name} · {r.owner?.student_class}-{r.owner?.section}</span>
                        ) : (
                            <span><span className="uppercase tracking-widest font-bold">Borrower · </span>{r.borrower?.name} · {r.borrower?.student_class}-{r.borrower?.section}</span>
                        )}
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(r.created_at).toLocaleString()}</span>
                    </div>

                    {/* Owner actions */}
                    {role === "owner" && r.status === "pending" && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            <button data-testid={`res-approve-${r.id}`} className="btn-accept" onClick={() => onAction(() => api.post(`/reservations/${r.id}/approve`), "Reservation approved. Share OTP with borrower to begin lending.")}>
                                <CheckCircle2 className="h-4 w-4" /> Approve
                            </button>
                            <button data-testid={`res-decline-${r.id}`} className="btn-decline" onClick={() => onAction(() => api.post(`/reservations/${r.id}/decline`), "Reservation declined.")}>
                                <XCircle className="h-4 w-4" /> Decline
                            </button>
                        </div>
                    )}

                    {/* Borrower shows the start_otp to give to owner */}
                    {role === "borrower" && r.status === "approved" && r.start_otp && (
                        <OtpDisplay label="Give this Start OTP to the owner" otp={r.start_otp} testid={`res-start-otp-${r.id}`} tone="library" note="The owner will enter this to commence the borrowing period." />
                    )}

                    {/* Owner enters start OTP */}
                    {role === "owner" && r.status === "approved" && (
                        <div className="mt-4 bg-paper border border-parchment rounded-xl p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-ink/60 mb-2">Enter Start OTP</div>
                            <p className="text-xs text-ink/60 mb-3">Ask the borrower for the 4-digit code, then enter it below to hand over the book.</p>
                            <div className="flex gap-2">
                                <input data-testid={`res-owner-otp-input-${r.id}`} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} maxLength={4} placeholder="0000" className="input-field font-mono text-2xl tracking-widest text-center max-w-[160px]" />
                                <button data-testid={`res-owner-start-btn-${r.id}`} className="btn-primary" onClick={() => onAction(() => api.post(`/reservations/${r.id}/start`, { otp }), "Book handed over. Keep your Return OTP safe until the book is returned.")}>
                                    <KeyRound className="h-4 w-4" /> Start borrowing
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Owner shows return_otp for borrower once borrowed */}
                    {role === "owner" && r.status === "borrowed" && r.return_otp && (
                        <OtpDisplay label="Return OTP · share with borrower on book return" otp={r.return_otp} testid={`res-return-otp-${r.id}`} tone="zapp" note="Give this only when you physically receive the book back." />
                    )}

                    {/* Borrower enters return OTP */}
                    {role === "borrower" && r.status === "borrowed" && (
                        <div className="mt-4 bg-paper border border-parchment rounded-xl p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-ink/60 mb-2">Return the book</div>
                            <p className="text-xs text-ink/60 mb-3">The owner will hand you a 4-digit Return OTP when they take the book back. Enter it to settle the coins.</p>
                            <div className="flex gap-2">
                                <input data-testid={`res-borrower-otp-input-${r.id}`} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} maxLength={4} placeholder="0000" className="input-field font-mono text-2xl tracking-widest text-center max-w-[160px]" />
                                <button data-testid={`res-borrower-return-btn-${r.id}`} className="btn-primary" onClick={() => onAction(() => api.post(`/reservations/${r.id}/return`, { otp }), "Book returned. Zapp coins settled.")}>
                                    <ArrowRightLeft className="h-4 w-4" /> Complete return
                                </button>
                            </div>
                        </div>
                    )}

                    {r.status === "returned" && (
                        <div className="mt-4 flex flex-wrap gap-2 items-center text-sm text-ink/70">
                            <span className="inline-flex items-center gap-1 text-library"><CheckCircle2 className="h-4 w-4" /> Returned</span>
                            {r.days_borrowed != null && (
                                <span className="text-ink/60">· {r.days_borrowed} day(s) · {r.total_cost} Zapp settled</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function OtpDisplay({ label, otp, testid, tone = "library", note }) {
    const bg = tone === "zapp" ? "bg-zapp/10 border-zapp/30" : "bg-library/10 border-library/30";
    const color = tone === "zapp" ? "text-zapp-dark" : "text-library";
    return (
        <div className={`mt-4 rounded-xl border p-4 ${bg}`} data-testid={testid}>
            <div className="text-xs uppercase tracking-widest font-bold text-ink/60 mb-2">{label}</div>
            <div className={`font-mono text-5xl font-bold tracking-[0.4em] ${color}`}>{otp}</div>
            {note && <p className="text-xs text-ink/60 mt-2">{note}</p>}
        </div>
    );
}
